import { useState } from 'react';
import {
  useApplyModelPricesMutation,
  useFetchModelPricesMutation,
  useListModelPricesQuery,
  useSetModelSelectableMutation
} from '@/services/api';
import type { ModelPriceProposalDto } from '@/types/api';
import { parseApiError } from '@/utils/apiError';
import { useToast } from '@/components/toast/ToastProvider';

function perMillion(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `$${(v * 1000).toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}`;
}

function dirClass(from: number | null, to: number | null): string {
  if (from === null || to === null || from === to) return '';
  return to > from ? 'mp-up' : 'mp-down';
}

function deltaDirection(from: number | null, to: number | null): string {
  if (from === null || to === null || from === 0) return '';
  return to > from ? ' ▲' : to < from ? ' ▼' : '';
}

function Switch({
  on,
  label,
  disabled,
  onToggle
}: {
  on: boolean;
  label: string;
  disabled?: boolean;
  onToggle: (next: boolean) => void;
}) {
  return (
    <label className="toml-toggle">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        disabled={disabled}
        className={`stage-switch ${on ? 'on' : ''}`}
        onClick={() => onToggle(!on)}
      >
        <span className="stage-switch-knob" />
      </button>
    </label>
  );
}

export function ModelConfigurationPanel({ canManage }: { canManage: boolean }) {
  const { data, isLoading } = useListModelPricesQuery(undefined, { skip: !canManage });
  const [fetchRates, fetchState] = useFetchModelPricesMutation();
  const [applyRates, applyState] = useApplyModelPricesMutation();
  const [setSelectable] = useSetModelSelectableMutation();
  const [proposal, setProposal] = useState<ModelPriceProposalDto | null>(null);
  const [chosen, setChosen] = useState<Record<string, boolean>>({});
  const [showAllNew, setShowAllNew] = useState(false);
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const onFetch = async () => {
    try {
      const res = await fetchRates().unwrap();
      setProposal(res);
      setShowAllNew(false);
      setChosen(Object.fromEntries(res.diff.changed.map((c) => [c.model, true])));
    } catch (err) {
      const e = parseApiError(err);
      toast.error(e.reason ?? e.message);
    }
  };

  const onApply = async () => {
    if (!proposal) return;
    const models = Object.entries(chosen)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (models.length === 0) return;
    try {
      const res = await applyRates({ proposal_id: proposal.proposal_id, models }).unwrap();
      toast.success(`Applied ${res.applied.length} rate(s) — version ${res.rate_version}`);
      setProposal(null);
      setChosen({});
    } catch (err) {
      const e = parseApiError(err);
      toast.error(e.reason ?? e.message);
    }
  };

  if (!canManage) return null;

  const rows = [...(data?.models ?? [])].sort((a, b) => a.model_id.localeCompare(b.model_id));
  const chosenCount = Object.values(chosen).filter(Boolean).length;
  const pendingCount = Object.keys(pending).length;
  const isSelectable = (model_id: string, stored: boolean) => pending[model_id] ?? stored;

  const onToggleSelectable = (model_id: string, stored: boolean, next: boolean) => {
    setPending((prev) => {
      const draft = { ...prev };
      if (next === stored) delete draft[model_id];
      else draft[model_id] = next;
      return draft;
    });
  };

  const onSaveSelectable = async () => {
    const models = Object.entries(pending).map(([model_id, selectable]) => ({
      model_id,
      selectable
    }));
    if (models.length === 0) return;
    setSaving(true);
    try {
      await setSelectable({ models }).unwrap();
      toast.success(`Saved ${models.length} model${models.length === 1 ? '' : 's'}`);
      setPending({});
    } catch (err) {
      const e = parseApiError(err);
      toast.error(e.reason ?? e.message);
    } finally {
      setSaving(false);
    }
  };
  const NEW_PREVIEW = 5;
  const added = proposal?.diff.added ?? [];
  const shownAdded = showAllNew ? added : added.slice(0, NEW_PREVIEW);

  return (
    <div className="card">
      <div className="card-head">
        <div className="card-title">Model rates</div>
        <div className="mp-head-actions">
          <button
            className="btn primary sm"
            onClick={onFetch}
            disabled={fetchState.isLoading}
          >
            {fetchState.isLoading ? 'Fetching…' : 'Fetch latest rates'}
          </button>
        </div>
      </div>

      <div id="model-rates-body">
        {isLoading && <div className="mp-note">Loading model rates…</div>}

          {!isLoading && (
            <>

              {proposal && !proposal.parse_ok && (
                <div className="alert error" role="alert">
                  <strong>Could not read the pricing page — nothing can be applied.</strong>
                  <ul>
                    {proposal.errors.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}

              {proposal && proposal.parse_ok && (
                <div className="mp-proposal">
                  <div className="mp-note">
                    Read {new Date(proposal.fetched_at).toLocaleString()} ·{' '}
                    <a href={proposal.url} target="_blank" rel="noreferrer">
                      source
                    </a>
                  </div>

                  {proposal.diff.changed.length === 0 && proposal.diff.added.length === 0 && (
                    <div className="mp-note">Everything stored matches the published page.</div>
                  )}

                  {proposal.diff.changed.length > 0 && (
                    <>
                      <div className="mp-section">Changed · {proposal.diff.changed.length}</div>
                      <table className="mp-table">
                        <thead>
                          <tr>
                            <th className="mp-col-toggle">apply</th>
                            <th>model</th>
                            <th>field</th>
                            <th className="mp-num">from / 1M</th>
                            <th className="mp-num">to / 1M</th>
                          </tr>
                        </thead>
                        <tbody>
                          {proposal.diff.changed.map((c) =>
                            c.deltas.map((d, i) => (
                              <tr key={`${c.model}-${d.field}`}>
                                <td>
                                  {i === 0 && (
                                    <Switch
                                      on={chosen[c.model] ?? false}
                                      label={`apply ${c.model}`}
                                      onToggle={(next) =>
                                        setChosen({ ...chosen, [c.model]: next })
                                      }
                                    />
                                  )}
                                </td>
                                <td className="mono">{i === 0 ? c.model : ''}</td>
                                <td className="mp-field">{d.field.replace('_per_1k', '')}</td>
                                <td className="mp-num mp-was">{perMillion(d.from)}</td>
                                <td className={`mp-num ${dirClass(d.from, d.to)}`}>
                                  {perMillion(d.to)}
                                  {deltaDirection(d.from, d.to)}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </>
                  )}

                  {added.length > 0 && (
                    <>
                      <div className="mp-section">
                        New · {added.length}
                        <span className="mp-note">not offered until marked selectable</span>
                      </div>
                      <table className="mp-table">
                        <thead>
                          <tr>
                            <th className="mp-col-toggle">add</th>
                            <th>model</th>
                            <th className="mp-num">input / 1M</th>
                            <th className="mp-num">output / 1M</th>
                          </tr>
                        </thead>
                        <tbody>
                          {shownAdded.map((a) => (
                            <tr key={a.model}>
                              <td>
                                <Switch
                                  on={chosen[a.model] ?? false}
                                  label={`add ${a.model}`}
                                  onToggle={(next) => setChosen({ ...chosen, [a.model]: next })}
                                />
                              </td>
                              <td className="mono">{a.model}</td>
                              <td className="mp-num">{perMillion(a.input_per_1k)}</td>
                              <td className="mp-num">{perMillion(a.output_per_1k)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {added.length > NEW_PREVIEW && (
                        <button
                          type="button"
                          className="btn sm ghost"
                          onClick={() => setShowAllNew(!showAllNew)}
                        >
                          {showAllNew
                            ? 'Show fewer'
                            : `Show all ${added.length} new models`}
                        </button>
                      )}
                    </>
                  )}

                  {proposal.diff.removed.length > 0 && (
                    <details>
                      <summary>
                        Priced here but not on the page ({proposal.diff.removed.length})
                      </summary>
                      <div className="mp-note">
                        Never removed automatically — projects and past cost records may still
                        depend on them.
                      </div>
                      <ul className="mp-list mono">
                        {proposal.diff.removed.map((r) => (
                          <li key={r.model}>{r.model}</li>
                        ))}
                      </ul>
                    </details>
                  )}

                  {proposal.api_list_error && (
                    <div className="alert warn" role="alert">
                      Could not read the provider's callable-model list ({proposal.api_list_error}),
                      so models were matched by name shape instead. Check any new model before
                      offering it.
                    </div>
                  )}

                  {proposal.not_dispatchable.length > 0 && (
                    <details>
                      <summary>
                        Priced but cannot run a stage ({proposal.not_dispatchable.length})
                      </summary>
                      <div className="mp-note">
                        Kept in the rate table so a stray call still prices, but never offered:
                        every agent runs through the Responses API with reasoning and tools.
                      </div>
                      <ul className="mp-list mono">
                        {proposal.not_dispatchable.map((m) => (
                          <li key={m}>{m}</li>
                        ))}
                      </ul>
                    </details>
                  )}

                  {proposal.unpriced_in_use.length > 0 && (
                    <div className="alert warn" role="alert">
                      <strong>In use here but absent from the published page:</strong>
                      <ul>
                        {proposal.unpriced_in_use.map((u) => (
                          <li key={u.model}>
                            {u.model} — {u.risk}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {proposal.warnings.length > 0 && (
                    <details>
                      <summary>{proposal.warnings.length} parser warning(s)</summary>
                      <ul className="mp-list mono">
                        {proposal.warnings.map((w) => (
                          <li key={w}>{w}</li>
                        ))}
                      </ul>
                    </details>
                  )}

                  <div className="mp-actions">
                    <button
                      className="btn primary sm"
                      onClick={onApply}
                      disabled={chosenCount === 0 || applyState.isLoading}
                    >
                      {applyState.isLoading ? 'Applying…' : `Apply ${chosenCount} selected`}
                    </button>
                    <button className="btn sm ghost" onClick={() => setProposal(null)}>
                      Discard
                    </button>
                  </div>
                </div>
              )}

              <details className="mp-details">
                <summary>Stored rates ({rows.length})</summary>
                <table className="mp-table">
                  <thead>
                    <tr>
                      <th>model</th>
                      <th className="mp-num">input / 1M</th>
                      <th className="mp-num">cached</th>
                      <th className="mp-num">cache write</th>
                      <th className="mp-num">output</th>
                      <th className="mp-col-toggle">selectable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.model_id} className={pending[r.model_id] !== undefined ? 'mp-dirty' : ''}>
                        <td className="mono">
                          {r.label ?? r.model_id}
                          {r.expires_at && <span className="mp-chip">to {r.expires_at}</span>}
                        </td>
                        <td className="mp-num">{perMillion(r.input_per_1k)}</td>
                        <td className="mp-num">{perMillion(r.cached_per_1k)}</td>
                        <td className="mp-num">{perMillion(r.cache_write_per_1k)}</td>
                        <td className="mp-num">{perMillion(r.output_per_1k)}</td>
                        <td className="mp-col-toggle">
                          <Switch
                            on={isSelectable(r.model_id, r.selectable === true)}
                            label={`selectable ${r.model_id}`}
                            disabled={!canManage || saving}
                            onToggle={(next) =>
                              onToggleSelectable(r.model_id, r.selectable === true, next)
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="save-bar" aria-label="model rate actions">
                  {pendingCount > 0 && (
                    <button
                      type="button"
                      className="btn sm ghost"
                      disabled={saving}
                      onClick={() => setPending({})}
                    >
                      Discard
                    </button>
                  )}
                  <button
                    className="btn primary sm"
                    disabled={pendingCount === 0 || saving}
                    onClick={() => void onSaveSelectable()}
                  >
                    {saving ? 'Saving…' : `Save changes (${pendingCount})`}
                  </button>
                </div>
              </details>
            </>
          )}
      </div>
    </div>
  );
}
